import React from "react";
import type { EmailExpress2Version, EmailExpress2VersionAttachment } from "@shared/schema";
import { sortEmailExpress2VersionsChronologically } from "@shared/email-express-2-conversation";

const VERSION_KIND_LABELS: Record<string, string> = {
  create: "Initial design",
  chat_revision: "Chat revision",
  manual_save: "Manual edit",
};

function safeHttpUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

function EmptyValue() {
  return <span className="italic text-muted-foreground">Not provided</span>;
}

export interface EmailExpress2StaticInputsProps {
  product: string | null;
  persona: string | null;
  angle: string | null;
  offer: string | null;
  campaignType: string;
  emailType: string;
  brief: string;
  ctaUrl: string | null;
  referenceImageUrl: string | null;
}

export function EmailExpress2StaticInputs(props: EmailExpress2StaticInputsProps) {
  const ctaUrl = safeHttpUrl(props.ctaUrl);
  const referenceImageUrl = safeHttpUrl(props.referenceImageUrl);
  return (
    <dl className="space-y-3" data-testid="panel-static-inputs">
      {[
        ["Product", props.product],
        ["Persona / audience", props.persona],
        ["Angle", props.angle],
        ["Offer", props.offer],
        ["Campaign type", props.campaignType],
        ["Email type", props.emailType],
      ].map(([label, value]) => (
        <div key={label} className="space-y-0.5">
          <dt className="text-[11px] font-medium text-muted-foreground">{label}</dt>
          <dd className="whitespace-pre-wrap break-words text-sm">{value || <EmptyValue />}</dd>
        </div>
      ))}
      <div className="space-y-0.5">
        <dt className="text-[11px] font-medium text-muted-foreground">Brief</dt>
        <dd
          className="max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-muted/40 p-2 text-sm"
          data-testid="text-static-brief"
        >
          {props.brief || <EmptyValue />}
        </dd>
      </div>
      <div className="space-y-0.5">
        <dt className="text-[11px] font-medium text-muted-foreground">CTA destination</dt>
        <dd className="break-all text-sm">
          {ctaUrl
            ? <a className="text-primary underline" href={ctaUrl} target="_blank" rel="noreferrer">{props.ctaUrl}</a>
            : <EmptyValue />}
        </dd>
      </div>
      <div className="space-y-1">
        <dt className="text-[11px] font-medium text-muted-foreground">Reference image</dt>
        <dd>
          {referenceImageUrl ? (
            <a href={referenceImageUrl} target="_blank" rel="noreferrer">
              <img src={referenceImageUrl} alt="Campaign reference" className="h-20 w-20 rounded-md border object-cover" />
            </a>
          ) : <EmptyValue />}
        </dd>
      </div>
    </dl>
  );
}

type PendingRevision = {
  instruction: string;
  attachments: EmailExpress2VersionAttachment[];
};

interface EmailExpress2RevisionConversationProps {
  versions: EmailExpress2Version[];
  pendingRevision: PendingRevision | null;
  isGenerating: boolean;
  isFailed: boolean;
  onOpenVersion: (version: EmailExpress2Version) => void;
  formatTimestamp?: (value: Date | string) => string;
}

export function EmailExpress2RevisionConversation({
  versions,
  pendingRevision,
  isGenerating,
  isFailed,
  onOpenVersion,
  formatTimestamp = (value) => new Date(value).toLocaleString(),
}: EmailExpress2RevisionConversationProps) {
  const chronologicalVersions = sortEmailExpress2VersionsChronologically(versions);
  return (
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto" data-testid="revision-conversation">
      {chronologicalVersions.length === 0 && (
        <p className="text-xs text-muted-foreground">Generation history will appear here.</p>
      )}
      {chronologicalVersions.map((version) => version.kind === "chat_revision" ? (
        <button
          key={version.id}
          type="button"
          onClick={() => onOpenVersion(version)}
          className="ml-auto block w-[88%] rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-left text-primary-foreground hover:bg-primary/90"
          data-testid={`button-version-${version.id}`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium">You</span>
            <span className="text-[10px] opacity-75">
              {version.createdAt ? formatTimestamp(version.createdAt) : ""}
            </span>
          </div>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm">
            {version.chatInstruction || version.changeSummary}
          </p>
          {Array.isArray(version.attachmentsJson) && version.attachmentsJson.length > 0 && (
            <span className="mt-2 flex flex-wrap gap-1" data-testid={`version-attachments-${version.id}`}>
              {(version.attachmentsJson as EmailExpress2VersionAttachment[]).map((attachment) => (
                <img
                  key={attachment.id}
                  src={attachment.url}
                  alt={attachment.fileName}
                  className="h-14 w-14 rounded-md border border-primary-foreground/30 object-cover"
                />
              ))}
            </span>
          )}
          <span className="mt-1 block text-[10px] opacity-75">Revision completed · View version</span>
        </button>
      ) : (
        <button
          key={version.id}
          type="button"
          onClick={() => onOpenVersion(version)}
          className="mx-auto block rounded-full border bg-muted/40 px-3 py-1.5 text-center text-[11px] text-muted-foreground"
          data-testid={`button-version-${version.id}`}
        >
          <span className="font-medium text-foreground">{VERSION_KIND_LABELS[version.kind] || version.kind}</span>
          {" · "}{version.createdAt ? formatTimestamp(version.createdAt) : ""}
        </button>
      ))}
      {pendingRevision && isGenerating && (
        <div className="ml-auto w-[88%] rounded-2xl rounded-br-sm bg-primary/80 px-3 py-2 text-primary-foreground" data-testid="revision-pending-message">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">You</span>
            <span className="text-[10px]">Generating…</span>
          </div>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm">{pendingRevision.instruction}</p>
          {pendingRevision.attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {pendingRevision.attachments.map((attachment) => (
                <img key={attachment.id} src={attachment.url} alt={attachment.fileName} className="h-14 w-14 rounded-md border object-cover" />
              ))}
            </div>
          )}
        </div>
      )}
      {isFailed && (
        <div className="mx-auto rounded-full border border-destructive/30 bg-destructive/10 px-3 py-1.5 text-center text-[11px] text-destructive">
          The latest revision failed. Your attachments are ready to retry.
        </div>
      )}
    </div>
  );
}